import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import type { AuthProviderContext, AuthProviderProps } from '@newfront-insurance/next-auth';
import type { Provider } from '@newfront-insurance/react-provision';
import { useProvider } from '@newfront-insurance/react-provision';
import type { ConfigType } from '@newfront-insurance/shared-public-config';
import { getConfig, getEnvFromUrl } from '@newfront-insurance/shared-public-config';
import { useEffect, type JSX } from 'react';

// eslint-disable-next-line no-process-env
const isRumEnabled = typeof window !== 'undefined' && process.env.NODE_ENV === 'production';

const ERRORS_TO_IGNORE: (string | RegExp)[] = [
  'ResizeObserver',
  'Abort fetching component for route',
  'Abort route change by user',
  'Route did not complete loading',
  'Failed to lookup route',
  '[LaunchDarkly]',
  'Failed to load script',
  'Failed to fetch',
  'Network Error',
  'The user aborted a request',
  'Uncaught "Script error."',
  /Uncaught "Object Not Found Matching Id:\d+, MethodName:update, ParamCount:\d+"/,
  '"undefined" is not valid JSON',
  'Invalid call to runtime.sendMessage().',
];

const rumAppNames: Record<keyof ConfigType['DATADOG']['RUM'], string> = {
  ACCOUNTS: 'account-app',
  BULK_IMPORT: 'bulk-import-app',
  CLAIMS: 'claims-app',
  CLIENT_DASH: 'client-dash-app',
  CONVERSION: 'conversion-app',
  FILES: 'files-app',
  NOVA_ADDINS: 'nova-addins-app',
  PAYMENTS: 'payments-app',
  PROOF_OF_INSURANCE: 'proof-of-insurance-app',
  PROPOSAL: 'proposal-app',
  QUOTING: 'quoting-app',
  TRADING_PARTNERS: 'trading-partners-app',
};

export function initializeDatadogLogsAndRUM(appName: keyof ConfigType['DATADOG']['RUM']): void {
  if (typeof window === 'undefined') {
    return;
  }

  const env = getEnvFromUrl();
  const config = getConfig();
  const rumConfig = config.DATADOG.RUM[appName];
  const rumAppName = rumAppNames[appName];

  // initializes both logs and rum
  if (isRumEnabled) {
    datadogLogs.init({
      clientToken: rumConfig.CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: rumAppName,

      env,
      enableExperimentalFeatures: ['feature_flags'],
      // eslint-disable-next-line no-process-env
      version: process.env.NEXT_PUBLIC_COMMIT_SHA,
      sessionSampleRate: 100,
      trackAnonymousUser: true,
      allowedTrackingOrigins: [window.location.origin],
      forwardErrorsToLogs: true,
      forwardReports: 'all',
      forwardConsoleLogs: 'all',
    });
    datadogLogs.logger.setLevel('info');

    datadogRum.init({
      applicationId: rumConfig.APPLICATION_ID,
      clientToken: rumConfig.CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: rumAppName,
      env,
      allowedTracingUrls: [window.location.origin, config.API.GRAPHQL_BFF],
      // eslint-disable-next-line no-process-env
      version: process.env.NEXT_PUBLIC_COMMIT_SHA,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      allowedGraphQlUrls: [config.API.GRAPHQL_BFF],
      traceSampleRate: 100,
      defaultPrivacyLevel: 'allow',
      beforeSend(event) {
        if (event.type === 'error') {
          if (
            ERRORS_TO_IGNORE.some((errorToIgnore) => {
              if (errorToIgnore instanceof RegExp) {
                return errorToIgnore.test(event.error.message);
              }
              return event.error.message.includes(errorToIgnore);
            })
          ) {
            return false;
          }
        }

        return true;
      },
    });

    datadogRum.startSessionReplayRecording();
  }
}

interface UserData {
  uuid: string;
  email: string;
  firstName: string;
  lastName: string;
}

function setDatadogSessionUser(user?: UserData): void {
  if (user) {
    const userData = {
      email: user.email,
      id: user.uuid,
      name: `${user.firstName} ${user.lastName}`,
    };
    // Example of how to log with datadog
    browserLogger.info('Setting datadog user', userData);
    datadogRum.setUser(userData);
    datadogLogs.setUser(userData);
  }
}

interface Props {
  authProvider: Provider<AuthProviderContext, AuthProviderProps>;
  children: JSX.Element;
}

export function DatadogRumWrapper({ authProvider, children }: Props) {
  const { userDetails } = useProvider(authProvider);

  useEffect(() => {
    if (userDetails && isRumEnabled) {
      setDatadogSessionUser(userDetails);
    }
  }, [userDetails]);

  return children;
}

interface ViewWrapperProps {
  viewName?: string;
  service: string;
  children: JSX.Element;
}

/**
 * Used to apply a custom name and service to a view
 */
export function DataDogViewWrapper({ viewName, service, children }: ViewWrapperProps): JSX.Element {
  useEffect(() => {
    if (viewName) {
      datadogRum.startView({ name: viewName, service });
    } else {
      datadogRum.startView({ service });
    }
  }, [viewName, service]);
  return children;
}

// If we are not in prod, we want to log to the console instead of datadog
const browserLogger = isRumEnabled
  ? datadogLogs.createLogger('browser', {
      level: 'info',
      context: {
        env: getEnvFromUrl(),
      },
    })
  : console;

export { datadogRum, browserLogger };
